import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import { Mail } from 'lucide-react';
import { Button, Card, EmptyState, PageHeader, Badge } from '@/components/ui';
import { useSession } from '@/lib/session';
import { useOrgs } from '@/lib/hooks';
import { reducers } from '@/lib/stdb';
import { attempt } from '@/lib/toast';
import { timeAgo } from '@/lib/format';

export function InvitesPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const highlight = params.get('invite');
  const email = useSession((s) => s.user?.email?.toLowerCase());
  const { roles } = useOrgs();
  const [invites] = useTable(tables.orgInvite);
  const mine = useMemo(
    () => invites.filter((i) => i.email === email && i.status === 'pending' && !roles.has(i.orgId)),
    [invites, email, roles],
  );
  return (
    <>
      <PageHeader title="Invitations" subtitle="Organizations that invited you" />
      {mine.length === 0 ? (
        <EmptyState icon={<Mail size={32} />} title="No pending invitations">
          {highlight ? 'This invitation was already used, revoked or sent to a different email address.' : 'When someone invites you, it shows up here.'}
        </EmptyState>
      ) : (
        <Card className="divide-y divide-line">
          {mine.map((inv) => (
            <div key={inv.id} className={`flex items-center gap-4 px-4 py-4 ${inv.id === highlight ? 'bg-accent/5' : ''}`}>
              <div className="flex-1">
                <div className="font-medium">{inv.orgName}</div>
                <div className="text-xs text-faint">
                  {inv.invitedByName} invited you {timeAgo(inv.createdAt)}{' '}
                  {inv.grantTargetName ? (
                    <>
                      to {inv.grantRole === 'editor' ? 'edit' : 'view'} <b className="text-fg">{inv.grantTargetName}</b>
                    </>
                  ) : (
                    <>
                      as <Badge>{inv.role}</Badge>
                    </>
                  )}
                </div>
              </div>
              <Button variant="ghost" onClick={() => void attempt(() => reducers().declineInvite({ inviteId: inv.id }))}>
                Decline
              </Button>
              <Button
                variant="primary"
                onClick={async () => {
                  if (await attempt(() => reducers().acceptInvite({ inviteId: inv.id }), `Welcome to ${inv.orgName}`)) {
                    navigate(`/orgs/${inv.orgId}`);
                  }
                }}
              >
                Accept
              </Button>
            </div>
          ))}
        </Card>
      )}
    </>
  );
}
